import { useCallback, useMemo, useState } from 'react';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import {
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/esm/useAsync';
import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
  radii,
} from '../../../theme/tokens';
import { EntityRefChips } from '../../common/EntityRefChips';
import { emailToUserRef, useUsersByEmail } from '../../common/useUsersByEmail';
import { EntityPanelFrame } from './EntityPanelFrame';
import { useTeamOpsGenie, type TeamAlert } from './useTeamOpsGenie';

const priorityConfig: Record<
  string,
  { label: string; color: string; bg: string }
> = {
  P1: { label: 'P1', color: '#fff', bg: '#e74c3c' },
  P2: { label: 'P2', color: '#fff', bg: '#e67e22' },
  P3: { label: 'P3', color: '#fff', bg: '#f1c40f' },
  P4: { label: 'P4', color: '#fff', bg: '#3498db' },
  P5: { label: 'P5', color: '#fff', bg: '#95a5a6' },
};

const timeAgo = (dateStr: string): string => {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
};

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

const AlertRow = ({ alert }: { alert: TeamAlert }) => {
  const config = priorityConfig[alert.priority] ?? priorityConfig.P5;

  return (
    <Link
      href={`https://meltwater.app.opsgenie.com/alert/detail/${alert.id}`}
      target="_blank"
      rel="noreferrer"
      underline="none"
      color="inherit"
      sx={{
        display: 'block',
        py: 1.5,
        transition: 'opacity 150ms ease',
        '&:hover': { opacity: 0.75 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <Chip
          label={config.label}
          size="small"
          sx={{
            backgroundColor: config.bg,
            borderRadius: '4px',
            color: config.color,
            fontSize: '0.65rem',
            fontWeight: 700,
            height: 20,
            letterSpacing: '0.03em',
          }}
        />
        {alert.acknowledged && (
          <Typography
            variant="caption"
            color="success.main"
            sx={{ fontWeight: 600 }}
          >
            ACK
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {timeAgo(alert.createdAt)}
        </Typography>
      </Box>
      <Typography variant="subtitle2" sx={{ lineHeight: 1.4 }}>
        {alert.message}
      </Typography>
      {alert.owner && (
        <Typography variant="caption" color="text.secondary">
          {alert.owner.replace(/@meltwater\.com$/, '')}
        </Typography>
      )}
    </Link>
  );
};

export const EntityOpsGenieCard = () => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const { entity } = useEntity();

  const opsgenieTeam = entity.metadata.annotations?.['opsgenie.com/team'];

  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const enableOverride =
    configApi.getOptionalBoolean('opsgenie.enableOnCallOverride') ?? false;

  const { value: data, loading } = useTeamOpsGenie(opsgenieTeam);

  const { value: identity } = useAsync(
    () => identityApi.getBackstageIdentity(),
    [identityApi],
  );
  const { value: profile } = useAsync(
    () => identityApi.getProfileInfo(),
    [identityApi],
  );

  const isMember = useMemo(() => {
    if (!identity) return false;
    const name = entity.metadata.name.toLowerCase();
    return identity.ownershipEntityRefs.some(
      ref =>
        ref.startsWith('group:') &&
        ref.split('/').pop()?.toLowerCase() === name,
    );
  }, [identity, entity.metadata.name]);

  const allParticipants = useMemo(
    () => [...new Set((data?.onCall ?? []).flatMap(s => s.participants))],
    [data],
  );

  const nextOnCall = useMemo(() => {
    const schedule = data?.onCall.find(s => s.nextParticipant && s.nextStartAt);
    if (!schedule?.nextParticipant || !schedule.nextStartAt) return undefined;
    return {
      participant: schedule.nextParticipant,
      startAt: schedule.nextStartAt,
    };
  }, [data]);

  const emails = useMemo(
    () => [
      ...new Set([
        ...allParticipants,
        ...(nextOnCall ? [nextOnCall.participant] : []),
      ]),
    ],
    [allParticipants, nextOnCall],
  );
  const { userMap } = useUsersByEmail(emails);

  const [overrideState, setOverrideState] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');

  const handleOverride = useCallback(async () => {
    if (!profile?.email || !data?.onCall.length) return;
    setOverrideState('loading');
    try {
      const proxyBase = await discoveryApi.getBaseUrl('proxy');
      const now = new Date();
      const end = new Date(now.getTime() + 3_600_000);
      await Promise.all(
        data.onCall.map(s =>
          fetchApi.fetch(
            `${proxyBase}/opsgenie/v2/schedules/${s.scheduleId}/overrides`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                alias: `backstage-override-${now.getTime()}`,
                user: { username: profile.email },
                startDate: now.toISOString(),
                endDate: end.toISOString(),
              }),
            },
          ),
        ),
      );
      setOverrideState('success');
      setTimeout(() => setOverrideState('idle'), 3000);
    } catch {
      setOverrideState('error');
      setTimeout(() => setOverrideState('idle'), 3000);
    }
  }, [profile, data, discoveryApi, fetchApi]);

  if (!opsgenieTeam) return null;

  return (
    <EntityPanelFrame title="OpsGenie">
      {loading ? (
        <Stack spacing={1.5}>
          <Skeleton variant="rounded" height={60} />
          <Skeleton variant="rounded" height={60} />
        </Stack>
      ) : !data ? (
        <Typography color="text.secondary" variant="body2">
          Unable to load OpsGenie data.
        </Typography>
      ) : (
        <Stack spacing={0} divider={<Divider />}>
          {/* On-call section */}
          <Box sx={{ pb: 2 }}>
            {data.onCall.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <PhoneInTalkIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                <Typography color="text.secondary" variant="body2">
                  No schedules found.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: nextOnCall ? '1fr 1fr' : '1fr',
                    columnGap: 2,
                    rowGap: 1,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                  >
                    <PhoneInTalkIcon
                      sx={{ color: 'primary.main', fontSize: 18 }}
                    />
                    <Typography variant="subtitle2">On-call</Typography>
                  </Box>

                  {nextOnCall && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        sx={{
                          color: tokens.textMuted,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                        }}
                      >
                        Next · {formatDate(nextOnCall.startAt)}
                      </Typography>
                    </Box>
                  )}

                  <EntityRefChips
                    refs={allParticipants.map(emailToUserRef)}
                    entityMap={userMap}
                    emptyText="No one on-call"
                  />

                  {nextOnCall && (
                    <EntityRefChips
                      refs={[emailToUserRef(nextOnCall.participant)]}
                      entityMap={userMap}
                      emptyText=""
                    />
                  )}
                </Box>

                {isMember && enableOverride && (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={
                      overrideState === 'loading' || overrideState === 'success'
                    }
                    onClick={handleOverride}
                    sx={{
                      alignSelf: 'flex-start',
                      fontSize: 12,
                      textTransform: 'none',
                    }}
                  >
                    {overrideState === 'loading'
                      ? 'Taking on-call…'
                      : overrideState === 'success'
                        ? 'On-call taken for 1 hour'
                        : overrideState === 'error'
                          ? 'Failed — try again'
                          : 'Take on-call for 1 hour'}
                  </Button>
                )}
              </Stack>
            )}
          </Box>

          {/* Alerts section */}
          <Box sx={{ pt: 2 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}
            >
              <WarningAmberIcon
                sx={{
                  color: data.alerts.length ? 'warning.main' : 'text.disabled',
                  fontSize: 18,
                }}
              />
              <Typography variant="subtitle2">Open alerts</Typography>
              {data.alerts.length > 0 && (
                <Typography
                  variant="caption"
                  sx={{ color: tokens.textMuted, fontWeight: 600 }}
                >
                  {data.alerts.length}
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              <Link
                href={`https://meltwater.app.opsgenie.com/alert?query=status%3Aopen%20teams%3A${encodeURIComponent(
                  opsgenieTeam,
                )}`}
                target="_blank"
                rel="noreferrer"
                underline="hover"
                variant="caption"
                sx={{ fontWeight: 600 }}
              >
                All alerts →
              </Link>
            </Box>
            {data.alerts.length === 0 ? (
              <Box
                sx={{
                  backgroundColor: getSurfaceColor(theme, 'subtle'),
                  border: `1px solid ${getSurfaceBorder(theme)}`,
                  borderRadius: `${radii.md}px`,
                  px: 2,
                  py: 1.5,
                  textAlign: 'center',
                }}
              >
                <Typography color="text.secondary" variant="body2">
                  No open alerts
                </Typography>
              </Box>
            ) : (
              <Stack
                divider={
                  <Box
                    sx={{ borderTop: `1px solid ${getSurfaceBorder(theme)}` }}
                  />
                }
                spacing={0}
              >
                {data.alerts.map(alert => (
                  <AlertRow key={alert.id} alert={alert} />
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </EntityPanelFrame>
  );
};
